import { FC, ReactNode } from 'react';

import { Button, HSpacingContainer, Text, VSpacing } from '@hh.ru/magritte-ui';
import { MinusOutlinedSize24, PlusOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import { MoneyInput, MoneyInputProps } from 'components/common/MoneyInput/MoneyInput';

export type MoneyInputWithStepsProps = MoneyInputProps & {
    value: string;
    step: number;
    onChange: (value: string) => void;
    onMinusClick?: () => void;
    onPlusClick?: () => void;
    dataQa?: string;
    minValue?: number;
    minusDataQa?: string;
    plusDataQa?: string;
    invalid?: boolean;
    errorMessage?: ReactNode;
    description?: ReactNode;
    disabled?: boolean;
};

export const MoneyInputWithSteps: FC<MoneyInputWithStepsProps> = ({
    value,
    step,
    onChange,
    onMinusClick,
    onPlusClick,
    dataQa,
    minValue = 0,
    minusDataQa = 'minus-button',
    plusDataQa = 'plus-button',
    invalid,
    errorMessage,
    description,
    disabled,
    ...inputProps
}) => {
    const numberValue = Number(value);

    const handleMinus = () => {
        onMinusClick?.();

        if ((minValue || minValue === 0) && numberValue - step <= minValue) {
            onChange(String(minValue));
            return;
        }

        onChange(String(numberValue - step));
    };

    const handlePlus = () => {
        onPlusClick?.();

        if (numberValue + step < minValue) {
            onChange(String(minValue));
            return;
        }

        onChange(String(numberValue + step));
    };

    return (
        <>
            <HSpacingContainer default={12}>
                <MoneyInput value={value} onChange={onChange} dataQa={dataQa} disabled={disabled} {...inputProps} />
                <Button
                    onClick={handleMinus}
                    mode="secondary"
                    style="neutral"
                    hideLabel
                    data-qa={minusDataQa}
                    icon={<MinusOutlinedSize24 />}
                    disabled={disabled}
                />
                <Button
                    onClick={handlePlus}
                    mode="secondary"
                    style="neutral"
                    hideLabel
                    data-qa={plusDataQa}
                    icon={<PlusOutlinedSize24 />}
                    disabled={disabled}
                />
            </HSpacingContainer>
            {invalid && errorMessage && (
                <>
                    <VSpacing default={8} />
                    <Text typography="label-3-regular" style="negative" data-qa={`${dataQa || ''}-error`}>
                        {errorMessage}
                    </Text>
                </>
            )}
            {description && !invalid && (
                <>
                    <VSpacing default={8} />
                    <Text typography="label-3-regular" style="secondary" data-qa={`${dataQa || ''}-description`}>
                        {description}
                    </Text>
                </>
            )}
        </>
    );
};
